import { MelodyLib } from '@tunasong/music-lib'
import type { MelodyGrid } from '@tunasong/schemas'
import { tempoFromTaps } from './tap-downbeat.js'

/**
 * From a free take and a set of taps to the grid the take will be quantized on.
 *
 * The taps give the starting point and nothing more: `tempoFromTaps` reads a median interval and a
 * downbeat, and `fitBeatGrid` then moves both until they explain the ONSETS. §11.2 step 3 is the
 * whole of this file's reason to exist - a grid taken straight from the taps would make every
 * `offsetMs` a record of the tapping rather than of the singing.
 *
 * Pure, and deliberately React-free: the fit is this feature's one musical decision and has to be
 * testable without a dialog, a microphone or an audio context.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §11.2
 */

export interface FreeTempoFit {
  bpm: number
  /** Where beat 1 falls on the raw take's clock. */
  phaseMs: number
  grid: MelodyGrid
  rmsDeviationMs: number
  verdict: MelodyLib.CaptureFitVerdict
}

/**
 * `null` when the taps cannot support a fit - fewer than `MIN_TAPS`, or a run with no interval.
 *
 * There is no partial answer here on purpose: a tempo guessed from two taps is exactly the silent
 * decision §11.1 rejects, and it would be wrong in the way that matters - the Trainer would later
 * score the singer against it.
 */
export const fitFreeTake = (raw: MelodyLib.RawNote[], tapsMs: number[]): FreeTempoFit | null => {
  const tapped = tempoFromTaps(tapsMs)
  if (!tapped) {
    return null
  }

  const onsetsMs = raw.map(note => note.onsetMs)
  const { bpm, phaseMs, rmsDeviationMs } = MelodyLib.fitBeatGrid(onsetsMs, {
    tappedBpm: tapped.bpm,
    tappedPhaseMs: tapped.downbeatMs,
  })

  return {
    bpm,
    phaseMs,
    grid: MelodyLib.chooseGrid(onsetsMs, bpm, phaseMs),
    rmsDeviationMs,
    verdict: MelodyLib.captureFitVerdict(rmsDeviationMs),
  }
}

/**
 * Re-base a take on its origin, which is the frame `quantizeRawNotes` reads (§5: onsets are ms from
 * beat 1).
 *
 * The origin is NOT always the fitted downbeat: a take that starts with a pickup gets an origin a
 * whole bar or more earlier, so the anacrusis has somewhere to live. `MelodyLib.originForPickup`
 * decides that; this function only subtracts. Nothing is clamped here either way - the caller picks
 * the origin, and by the time it does, no note is early enough to be dropped.
 */
export const rawNotesFromDownbeat = (raw: MelodyLib.RawNote[], phaseMs: number): MelodyLib.RawNote[] =>
  raw.map(note => ({ ...note, onsetMs: note.onsetMs - phaseMs }))
